/**
 * Action Event Registration System
 * Registers event listeners for all action functions
 */

// Import all action functions
import { runAddToCart } from './runAddToCart';
import { runAddToCartAndCartOpen } from './runAddToCartAndCartOpen';
import { runBackdropHide } from './runBackdropHide';
import { runBackdropShow } from './runBackdropShow';
import { runBackdropToggle } from './runBackdropToggle';
import { runBackgroundChanger } from './runBackgroundChanger';
import { runBlockVisibility } from './runBlockVisibility';
import { runCartClose } from './runCartClose';
import { runCartOpen } from './runCartOpen';
import { runCartRemoveProduct } from './runCartRemoveProduct';
import { runCartToggle } from './runCartToggle';
import { runCustomerAddressDelete } from './runCustomerAddressDelete';
import { runCustomerAddressUpdate } from './runCustomerAddressUpdate';
import { runCustomerDetailsUpdate } from './runCustomerDetailsUpdate';
import { runCustomerForgotPassword } from './runCustomerForgotPassword';
import { runCustomerLogin } from './runCustomerLogin';
import { runCustomerRebuyOrder } from './runCustomerRebuyOrder';
import { runCustomerRegister } from './runCustomerRegister';
import { runCustomerSubscribe } from './runCustomerSubscribe';
import { runDrawerClose } from './runDrawerClose';
import { runDrawerOpen } from './runDrawerOpen';
import { runDrawerToggle } from './runDrawerToggle';
import { runElementHide } from './runElementHide';
import { runElementShow } from './runElementShow';
import { runFindInStore } from './runFindInStore';
import { runGetCurrency } from './runGetCurrency';
import { runInitializeVariants } from './runInitializeVariants';
import { runManageAddButton } from './runManageAddButton';
import { runManageQuantity } from './runManageQuantity';
import { runModelViewToggle } from './runModelViewToggle';
import { runOrderLookup } from './runOrderLookup';
import { runOpenQuickAdd } from './runOpenQuickAdd';
import { runOutOfStockNotificationSubscribe } from './runOutOfStockNotificationSubscribe';
import { runPopupClose } from './runPopupClose';
import { runPopupOpen } from './runPopupOpen';
// import { runPopupToggle } from './runPopupToggle';
import { runQuantityDecrease } from './runQuantityDecrease';
import { runQuantityIncrease } from './runQuantityIncrease';
import { runQuantitySet } from './runQuantitySet';
import { runQuickAddToCart } from './runQuickAddToCart';
import { runGetRecommendations } from './runGetRecommendations';
import { runRecentlyViewedAdd } from './runRecentlyViewedAdd';
import { runRecentlyViewedGet } from './runRecentlyViewedGet';
import { runRecentlyViewedManage } from './runRecentlyViewedManage';
import { runRemoveCartItem } from './runRemoveCartItem';
import { runScrollToTop } from './runScrollToTop';
import { runSearchAction } from './runSearchAction';
import { runSearchClear } from './runSearchClear';
import { runSearchClose } from './runSearchClose';
import { runSearchOpen } from './runSearchOpen';
import { runSearchToggle } from './runSearchToggle';
import { runSelectSellingPlan } from './runSelectSellingPlan';
import { runSelectVariant } from './runSelectVariant';
import { runSetCurrency } from './runSetCurrency';
import { runShowNotification } from './runShowNotification';
import { runStockLookup } from './runStockLookup';
import { runUpdateCartQuantity } from './runUpdateCartQuantity';
import { runVisibilityToggle } from './runVisibilityToggle';
import { runWishlistAdd } from './runWishlistAdd';
import { runWishlistGet } from './runWishlistGet';
import { runWishlistRemove } from './runWishlistRemove';
// import { runAddToWishlistWithContext } from './runAddToWishlistWithContext';

/**
 * Action registry mapping event names to functions
 */
const actionRegistry = {
  'arc:action:addToCart': runAddToCart,
  'arc:action:addToCartAndCartOpen': runAddToCartAndCartOpen,
  'arc:action:backdropHide': runBackdropHide,
  'arc:action:backdropShow': runBackdropShow,
  'arc:action:backdropToggle': runBackdropToggle,
  'arc:action:backgroundChanger': runBackgroundChanger,
  'arc:action:blockVisibility': runBlockVisibility,
  'arc:action:cartClose': runCartClose,
  'arc:action:cartOpen': runCartOpen,
  'arc:action:cartRemoveProduct': runCartRemoveProduct,
  'arc:action:cartToggle': runCartToggle,
  'arc:action:customerAddressDelete': runCustomerAddressDelete,
  'arc:action:customerAddressUpdate': runCustomerAddressUpdate,
  'arc:action:customerDetailsUpdate': runCustomerDetailsUpdate,
  'arc:action:customerForgotPassword': runCustomerForgotPassword,
  'arc:action:customerLogin': runCustomerLogin,
  'arc:action:customerRebuyOrder': runCustomerRebuyOrder,
  'arc:action:customerRegister': runCustomerRegister,
  'arc:action:customerSubscribe': runCustomerSubscribe,
  'arc:action:drawerClose': runDrawerClose,
  'arc:action:drawerOpen': runDrawerOpen,
  'arc:action:drawerToggle': runDrawerToggle,
  'arc:action:elementHide': runElementHide,
  'arc:action:elementShow': runElementShow,
  'arc:action:findInStore': runFindInStore,
  'arc:action:getCurrency': runGetCurrency,
  'arc:action:initializeVariants': runInitializeVariants,
  'arc:action:manageAddButton': runManageAddButton,
  'arc:action:manageQuantity': runManageQuantity,
  'arc:action:modelViewToggle': runModelViewToggle,
  'arc:action:orderLookup': runOrderLookup,
  'arc:action:openQuickAdd': runOpenQuickAdd,
  'arc:action:outOfStockNotificationSubscribe': runOutOfStockNotificationSubscribe,
  'arc:action:popupClose': runPopupClose,
  'arc:action:popupOpen': runPopupOpen,
  // 'arc:action:popupToggle': runPopupToggle,
  'arc:action:quantityDecrease': runQuantityDecrease,
  'arc:action:quantityIncrease': runQuantityIncrease,
  'arc:action:quantitySet': runQuantitySet,
  'arc:action:quickAddToCart': runQuickAddToCart,
  'arc:action:getRecommendations': runGetRecommendations,
  'arc:action:recentlyViewedAdd': runRecentlyViewedAdd,
  'arc:action:recentlyViewedGet': runRecentlyViewedGet,
  'arc:action:recentlyViewedManage': runRecentlyViewedManage,
  'arc:action:removeCartItem': runRemoveCartItem,
  'arc:action:scrollToTop': runScrollToTop,
  'arc:action:searchAction': runSearchAction,
  'arc:action:searchClear': runSearchClear,
  'arc:action:searchClose': runSearchClose,
  'arc:action:searchOpen': runSearchOpen,
  'arc:action:searchToggle': runSearchToggle,
  'arc:action:selectSellingPlan': runSelectSellingPlan,
  'arc:action:selectVariant': runSelectVariant,
  'arc:action:setCurrency': runSetCurrency,
  'arc:action:showNotification': runShowNotification,
  'arc:action:stockLookup': runStockLookup,
  'arc:action:updateCartQuantity': runUpdateCartQuantity,
  'arc:action:visibilityToggle': runVisibilityToggle,
  'arc:action:wishlistAdd': runWishlistAdd,
  'arc:action:wishlistGet': runWishlistGet,
  'arc:action:wishlistRemove': runWishlistRemove,
  // 'arc:action:addToWishlistWithContext': runAddToWishlistWithContext
};

/**
 * Register event listeners for all actions
 */
const registerActionListeners = () => {
  let registeredCount = 0;
  
  Object.entries(actionRegistry).forEach(([eventName, actionFunction]) => {
    if (typeof actionFunction === 'function') {
      window.addEventListener(eventName, async (event) => {
        try {
          if (window.enableDebugMode) { console.log(`🎬 Executing action for event: ${eventName}`, event.detail); }
          
          const result = await actionFunction(event.detail);
          
          // Dispatch success event
          window.dispatchEvent(new CustomEvent(`${eventName}:success`, {
            detail: result
          }));
          
          if (window.enableDebugMode) { console.log(`✅ Action completed successfully: ${eventName}`, result); }
          
        } catch (error) {
          console.error(`❌ Error executing action for ${eventName}:`, error);
          
          // Dispatch error event
          window.dispatchEvent(new CustomEvent(`${eventName}:error`, {
            detail: { 
              error: error.message,
              originalData: event.detail,
              stack: error.stack 
            }
          }));
        }
      });
      
      registeredCount++;
    } else {
      console.warn(`⚠️ Action function not found for: ${eventName}`);
    }
  });
  
  if (window.enableDebugMode) {
    console.log(`🚀 Action Event System Initialized: ${registeredCount} listeners registered`);
    console.log('📋 Registered actions:', Object.keys(actionRegistry).map(key => key.replace('arc:action:', '')));
  }
};

/**
 * Get all registered actions (for actionHelpers)
 */
window.getRegisteredActions = () => {
  return Object.keys(actionRegistry)
    .filter(eventName => typeof actionRegistry[eventName] === 'function')
    .map(eventName => ({
      eventName,
      actionName: eventName.replace('arc:action:', ''),
      functionName: actionRegistry[eventName].name,
      isRegistered: true
    }));
};

/**
 * Debug helper to test action system
 */
window.testActionSystem = async () => {
  if (window.enableDebugMode) { console.log('🧪 Testing Action System...'); }
  
  try {
    // Test a safe action
    const result = await new Promise((resolve, reject) => {
      const testData = {
        message: 'Action system test successful!',
        type: 'success',
        duration: 2000
      };
      
      const successHandler = (event) => {
        window.removeEventListener('arc:action:showNotification:success', successHandler);
        window.removeEventListener('arc:action:showNotification:error', errorHandler);
        resolve(event.detail);
      };
      
      const errorHandler = (event) => {
        window.removeEventListener('arc:action:showNotification:success', successHandler);
        window.removeEventListener('arc:action:showNotification:error', errorHandler);
        reject(new Error(event.detail.error));
      };
      
      window.addEventListener('arc:action:showNotification:success', successHandler, { once: true });
      window.addEventListener('arc:action:showNotification:error', errorHandler, { once: true });
      
      window.dispatchEvent(new CustomEvent('arc:action:showNotification', {
        detail: testData
      }));
    });
    
    if (window.enableDebugMode) { console.log('✅ Action system test passed:', result); }
    return true;
  } catch (error) {
    console.error('❌ Action system test failed:', error);
    return false;
  }
};

/**
 * Get action registry for debugging
 */
window.getActionRegistry = () => actionRegistry;

// Auto-register when module loads
if (typeof window !== 'undefined') {
  // Register immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', registerActionListeners);
  } else {
    registerActionListeners();
  }
  
  // Also register on alpine:init if Alpine.js is being used
  document.addEventListener('alpine:init', () => {
    if (window.enableDebugMode) { console.log('🏔️ Alpine.js detected - Action system ready for Alpine integration'); }
  });
}

export { actionRegistry, registerActionListeners };